/**
 * Input Component
 *
 * 텍스트/숫자 입력 필드 컴포넌트
 * - label, placeholder, error 지원
 * - inputMode로 모바일 키보드 타입 제어
 * - 최소 높이 48px (모바일 터치 영역)
 */

import type { ReactNode } from 'react'

export interface InputProps {
  label: string
  type?: 'text' | 'number' | 'email'
  value: string | number
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
  icon?: ReactNode
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email'
}

export function Input({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  error,
  required = false,
  disabled = false,
  icon,
  inputMode,
}: InputProps) {
  const hasError = !!error

  // 베이스 스타일
  const baseClasses = 'w-full h-12 px-4 border rounded-lg text-base transition-colors'

  // 에러 상태
  const borderClasses = hasError
    ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:focus:ring-primary-400'

  // 비활성화 상태
  const disabledClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed text-gray-500 dark:bg-gray-900 dark:text-gray-400'
    : 'bg-white dark:bg-gray-800 dark:text-gray-100'

  // 아이콘이 있을 때 패딩 조정
  const paddingClasses = icon ? 'pl-12' : 'px-4'

  const inputClassName = [baseClasses, borderClasses, disabledClasses, paddingClasses]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="w-full">
      {/* Label */}
      <label className="block mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </label>

      {/* Input Field */}
      <div className="relative">
        {/* 아이콘 (있을 경우) */}
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
            {icon}
          </div>
        )}

        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          inputMode={inputMode}
          className={inputClassName}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${label}-error` : undefined}
          data-error={hasError}
        />
      </div>

      {/* Error Message */}
      {hasError && (
        <p
          id={`${label}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}
